import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { skillKeys } from "@/lib/query";
import type { Category } from "@/lib/skills/types";
import { uploadArtifact } from "@/lib/skills/upload";

export interface SubmitMetadata {
  title: string;
  desc: string;
  categories: Category[];
  whatIs: string;
  whyImportant: string;
  example: string;
  install: string;
  notes?: string;
  version: string;
}

export function useSubmitSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, metadata }: { file: File; metadata: SubmitMetadata }) => {
      const key = await uploadArtifact(file);
      const res = await fetch("/api/submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...metadata, artifactKey: key }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.error === "string" ? j.error : "Submission failed");
      }
      const json: { data: { slug: string } } = await res.json();
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: skillKeys.all });
      qc.invalidateQueries({ queryKey: skillKeys.mine });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Submission failed"),
  });
}
